import { useCallback, useRef, useState } from "react";
import { ethers } from "ethers";

// ZNS registry lives on COTI mainnet only — .coti domains resolve against it
// regardless of which chain the app is connected to (same as zns-sdk).
const ZNS_REGISTRY_ADDRESS = "0x4293b8907d403b397A4050ebc92a4722aa600720";
const COTI_MAINNET_RPC = "https://mainnet.coti.io/rpc";

const ZNS_REGISTRY_ABI = [
  "function registryLookupByName(string domainName) view returns (tuple(address owner, string domainName, uint16 lengthOfDomain, uint256 expirationDate))",
  "error DomainExpired()",
];

export const isZNSDomain = (input: string): boolean =>
  input.toLowerCase().endsWith(".coti");

interface ZNSResolverState {
  address: string | null;
  isResolving: boolean;
  error: string | null;
}

const IDLE_STATE: ZNSResolverState = {
  address: null,
  isResolving: false,
  error: null,
};

/**
 * Resolves .coti domain names to their owner's address via the ZNS registry
 * on COTI mainnet. Unregistered names return the zero address from the
 * registry — treated as "not found" here so they never validate as a
 * recipient. Expired names revert with DomainExpired().
 */
export const useZNSResolver = () => {
  const [state, setState] = useState<ZNSResolverState>(IDLE_STATE);
  const requestIdRef = useRef(0);

  const resolveAddress = useCallback(async (domain: string): Promise<string | null> => {
    const requestId = ++requestIdRef.current;
    setState({ address: null, isResolving: true, error: null });

    try {
      const provider = new ethers.JsonRpcProvider(COTI_MAINNET_RPC);
      const registry = new ethers.Contract(ZNS_REGISTRY_ADDRESS, ZNS_REGISTRY_ABI, provider);
      const name = domain.split(".")[0];
      const result = await registry.registryLookupByName(name);
      const owner: string = result.owner;

      if (requestId !== requestIdRef.current) return null;

      if (!owner || owner === ethers.ZeroAddress || !ethers.isAddress(owner)) {
        setState({ address: null, isResolving: false, error: "No address found for this domain." });
        return null;
      }

      setState({ address: owner, isResolving: false, error: null });
      return owner;
    } catch (err: unknown) {
      if (requestId !== requestIdRef.current) return null;

      const revertName = (err as { revert?: { name?: string } })?.revert?.name;
      const message = err instanceof Error ? err.message : "";
      const friendly =
        revertName === "DomainExpired" || message.includes("DomainExpired")
          ? "This domain has expired."
          : "Could not resolve this domain.";
      setState({ address: null, isResolving: false, error: friendly });
      return null;
    }
  }, []);

  const clearResolver = useCallback(() => {
    requestIdRef.current++;
    setState(IDLE_STATE);
  }, []);

  return { ...state, resolveAddress, clearResolver };
};
